import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { I18N_KEYS } from '@/language/keys';
import { CheckMarkCircleIcon } from '@/UI/Icons';
import { useCreateSubflowTemplate } from '@/queries/useSubflowTemplates';
import { useWorkflowStore } from '@/state/workflow.state';
import { useGlobalStore } from '@/state/global.state';
import { SubflowTemplateType } from '@/types/api/subflowTemplate';
import { useFlowView } from '@/components/Recipe/FlowContext';
import { SaveAssetModal } from './SaveAssetModal';
import { getChangedParamsDescription, getNodeDefinitionId } from './getChangedParamsDescription';
import type { NodeId } from 'web';

interface SaveNodeModalProps {
  nodeId: NodeId;
  onClose: () => void;
}

export function SaveNodeModal({ nodeId, onClose }: SaveNodeModalProps) {
  const { t } = useTranslation();
  const { mutate, isPending } = useCreateSubflowTemplate();
  const updateSnackbarData = useGlobalStore((s) => s.updateSnackbarData);
  const flowView = useFlowView();

  const { name, description } = useMemo(() => {
    const node = flowView.getNode(nodeId);
    if (!node) {
      return { name: '', description: '' };
    }
    const nodeTypes = useWorkflowStore.getState().nodeTypes;
    const desc = getChangedParamsDescription(node, nodeTypes);
    return { name: node.data.name || '', description: desc };
  }, [flowView, nodeId]);

  const handleSave = useCallback(
    (saveName: string, saveDescription: string) => {
      const node = flowView.getNode(nodeId);
      if (!node) {
        return;
      }

      mutate(
        {
          type: SubflowTemplateType.SavedNode,
          name: saveName,
          description: saveDescription,
          data: { nodes: [node] },
          icon: node.type,
          sourceNodeDefinitionId: getNodeDefinitionId(node),
        },
        {
          onSuccess: () => {
            onClose();
            updateSnackbarData({
              text: t(I18N_KEYS.RECIPE_MAIN.NODES.NODE_MENU.SAVE_NODE_SUCCESS),
              isOpen: true,
              icon: <CheckMarkCircleIcon width={20} height={20} />,
            });
          },
          onError: () => {
            updateSnackbarData({
              text: t(I18N_KEYS.RECIPE_MAIN.NODES.NODE_MENU.SAVE_NODE_ERROR),
              isOpen: true,
            });
          },
        },
      );
    },
    [flowView, nodeId, mutate, onClose, updateSnackbarData, t],
  );

  return (
    <SaveAssetModal
      open
      onClose={onClose}
      onSave={handleSave}
      title={t(I18N_KEYS.RECIPE_MAIN.NODES.NODE_MENU.SAVE_NODE)}
      saveButtonLabel={t(I18N_KEYS.RECIPE_MAIN.NODES.NODE_MENU.SAVE_NODE)}
      initialName={name}
      initialDescription={description}
      isSaving={isPending}
    />
  );
}
